import {
  Form,
  IconQuestion,
  NumberField,
  PrimaryButton,
  Space,
  TertiaryButton,
  WithFormControlNumberField,
  WithTooltip,
} from '@procurify/ui'
import { type FormikHelpers, FormikProvider, useFormik } from 'formik'
import { noop } from 'lodash'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'

import messages from './messages'

export interface ITaxFormProps {
  onSubmit?(
    data: TaxFormSchema,
    formikHelpers: FormikHelpers<TaxFormSchema>
  ): void
  onCancel?(): void
}

export interface TaxFormSchema {
  name: string
  description: string
  isInclusive: boolean
  percentage: string
  percentageFloatValue: number
}

export const TaxForm = ({
  onSubmit = noop,
  onCancel = noop,
}: ITaxFormProps) => {
  const intl = useIntl()

  const validationSchema = Yup.object<TaxFormSchema>({
    name: Yup.string().required(intl.formatMessage(messages.nameRequiredError)),
    description: Yup.string(),
    isInclusive: Yup.boolean(),
    percentage: Yup.string().required(
      intl.formatMessage(messages.taxRateRequiredError)
    ),
    percentageFloatValue: Yup.number(),
  })

  const initialFormValues: TaxFormSchema = {
    name: '',
    description: '',
    isInclusive: false,
    percentage: '',
    percentageFloatValue: 0,
  }

  const formikContext = useFormik<TaxFormSchema>({
    initialValues: initialFormValues,
    validationSchema,
    onSubmit,
  })

  const { isSubmitting, setFieldValue } = formikContext

  return (
    <FormikProvider value={formikContext}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <Space>
            <Space direction='horizontal'>
              <Space.Item width={1 / 2}>
                <Form.TextField
                  id='name'
                  name='name'
                  placeholder={intl.formatMessage(
                    messages.nameFieldPlaceholder
                  )}
                  label={intl.formatMessage(messages.nameFieldLabel)}
                  required
                />
              </Space.Item>
              <Space.Item width={1 / 2}>
                <WithFormControlNumberField
                  id='percentage'
                  name='percentage'
                  label={intl.formatMessage(messages.taxRateLabel)}
                  required
                >
                  <NumberField
                    placeholder={intl.formatMessage(
                      messages.taxRatePlaceholder
                    )}
                    decimalPlaces={4}
                    onValueChange={({ floatValue }) => {
                      setFieldValue('percentageFloatValue', floatValue)
                    }}
                  />
                </WithFormControlNumberField>
              </Space.Item>
            </Space>
            <Space>
              <Form.Checkbox
                id='isInclusive'
                name='isInclusive'
                inputProps={{
                  'aria-label': intl.formatMessage(messages.inclusiveTaxLabel),
                }}
              >
                {intl.formatMessage(messages.inclusiveTaxLabel)}{' '}
                <WithTooltip
                  title={intl.formatMessage(
                    messages.inclusiveTaxTooltipMessage
                  )}
                  placement='top'
                >
                  <WithTooltip.Wrapper>
                    <IconQuestion
                      textAlign='center'
                      aria-label={intl.formatMessage(
                        messages.inclusiveTaxTooltipLabel
                      )}
                    />
                  </WithTooltip.Wrapper>
                </WithTooltip>
              </Form.Checkbox>
            </Space>
          </Space>

          <Space.Divider />
          <Space direction='horizontal' justify='end'>
            <TertiaryButton type='button' onClick={onCancel}>
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' disabled={isSubmitting}>
              {intl.formatMessage(messages.submitButtonLabel)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

TaxForm.displayName = 'TaxForm'
